import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import type { NativePaymentMethod, NativeSale } from '../../types/nativeSales';
import { formatMoney } from '../../utils/costFormatting';
import {
  POS_CARD_STEP,
  POS_CASH,
  POS_METHODS,
  cardPathOf,
  describeMethodPanel,
  describeSaleCounts,
  methodOf,
  type PosCardPath,
  type PosCashReceived,
  type PosPaymentChoice,
} from '../../utils/posView';
import type { ZettleNotice } from '../../utils/zettlePos';
import { Icon } from '../ui/Icon';
import { Badge, Button, Field, Input } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';
import { PosMethodTile, PosNotice, PosSpinner } from './PosParts';
import { fieldCompanion, revealElement } from '../../layouts/softKeyboard';
import { scrollParentOf } from './scrollParent';

// Rediseño · Bloque D — la pantalla de cobro (P-027), con B0-03 y B0-04. Solo dibuja: cada cosa que
// se toca avisa a quien la usa, que es quien registra, cancela o verifica.
//
// B0-03: el primer nivel son EXACTAMENTE cuatro métodos —Efectivo, Tarjeta, Transferencia y Otro—.
// «Tarjeta» abre un segundo paso: cobrarla con el lector (solo si este equipo lo tiene) o registrar
// que ya se cobró en una terminal de fuera. Ninguna de las dos es un método del primer nivel.
//
// B0-04: un cobro en duda no es un cobro fallido. Con el cobro sin resolver los métodos NO se
// dibujan —no basta con apagarlos— y quedan a la vista las dos salidas: «Verificar cobro» y «Dejar
// pendiente y seguir vendiendo».
//
// P-080 (D-P080-CASH-RECEIVED-VISUAL): en efectivo, «Recibí» y «Cambio». Son ayuda de la pantalla: lo
// recibido no se guarda ni viaja, y no cambia el total; el botón sigue diciendo el total de la venta.
//
// Lo que la pantalla aprobada trae y aquí NO está: «Atrás» (la venta ya está guardada y no se puede editar:
// se cancela o se deja pendiente); «Lector Zettle listo» (la app no sabe si hay un lector encendido);
// y el paso «Pago confirmado → Continuar» (cuando el servidor confirma, se pasa directo al éxito).
// Los botones conservan su nombre —«Confirmar cobro», «Verificar cobro», «Cancelar venta»— porque
// los avisos del cobro (`utils/zettlePos.ts`, el mapa de errores de `api/client.ts`) mandan a la
// persona a ellos citándolos por ese nombre, justo cuando hay dinero en duda.

export interface PosCheckoutProps {
  sale: NativeSale;
  /** Hay un cobro con tarjeta sin resolver: no se cobra de otra forma ni se cancela. */
  locked: boolean;
  /**
   * FI-53: el candado es de un cobro que el servidor ya CONFIRMÓ, con la venta todavía sin completar.
   * No se dice «en duda» ni «sin resolver», y el botón principal no cobra: completa la venta.
   */
  paid: boolean;
  /** Con `paid`: la venta no se pudo leer. Se afirma el cobro y de la venta nada, tampoco que falte completarla. */
  paidUnread: boolean;
  /** Hay una operación en curso (registrar, cancelar, verificar, el lector). */
  busy: boolean;
  canceling: boolean;
  /** El lector ya respondió y se está confirmando con el servidor. */
  confirming: boolean;
  /** La venta no salió del pedido que está en pantalla (se retomó): sus productos van a la vista. */
  showLines: boolean;
  error: { message: string; ambiguous: boolean } | undefined;
  notice: ZettleNotice | undefined;
  /**
   * «Verificar cobro» solo existe mientras la pantalla tiene la referencia del intento: el que abrió
   * aquí mismo, o la marca local que deja el lector, que solo sobrevive si el servidor no llegó a
   * recibir el resultado.
   */
  canVerify: boolean;
  choice: PosPaymentChoice;
  /** Un intento con tarjeta no cobró: hay que volver a elegir, y nada se ve elegido. */
  mustPick: boolean;
  /** P-081-C1: la persona todavía no eligió cómo le pagan (el cobro abre así): nada se ve elegido. */
  unpicked?: boolean;
  /** P-080: la terminal de fuera la eligió la pantalla, por ser el único camino de la tarjeta. */
  cardAuto: boolean;
  /** Este equipo tiene lector. */
  readerOffered: boolean;
  /** La leyenda del modo del lector: el build piloto cobra dinero real, y se nota. */
  readerHint: { text: string; live: boolean };
  /** Por qué el botón principal está apagado, cuando la razón es algo que la persona puede resolver. */
  why: string | undefined;
  /** Se tocó Atrás y no se salió (cobro en proceso, sin resolver, o una venta que ya pasó por el lector): se dice por qué. */
  backBlocked: boolean;
  /** P-081: el nivel del cobro: «¿Cómo te pagan?» o el panel de un método ya tocado. */
  payLevel: 'methods' | 'detail';
  /** P-081: sube cada vez que se toca un método, aunque ya estuviera elegido: su panel se trae a la vista. */
  revealTick: number;
  /** P-081: «‹ Atrás» de la pantalla; el mismo paso que el Atrás del teléfono y del navegador. */
  onBack: () => void;
  confirmDisabled: boolean;
  cancelDisabled: boolean;
  /** P-080: lo escrito en «Recibí» y lo que dice frente al total. Solo pantalla. */
  cash: { text: string; received: PosCashReceived };
  onCash: (text: string) => void;
  onMethod: (method: NativePaymentMethod) => void;
  onCardPath: (path: PosCardPath) => void;
  onConfirm: () => void;
  onRetry: () => void;
  onCancel: () => void;
  onVerify: () => void;
  onLeavePending: () => void;
  onLogin: () => void;
  onOpenSettings: () => void;
}

/** Con un cobro con tarjeta sin resolver el botón principal no cobra: dice qué se espera. */
const LOCKED_LABEL = 'Esperando al servidor…';
/** FI-53: con el cobro ya confirmado, lo único que queda por hacer. No cobra. */
const COMPLETE_LABEL = 'Completar la venta';
/** El aire que queda entre lo que se acaba de mostrar y el pie pegado. */
const REVEAL_MARGIN = 12;

export function PosCheckout(props: PosCheckoutProps) {
  const { sale, locked, paid, paidUnread, busy, canceling, confirming, choice, mustPick, cardAuto } = props;
  const total = formatMoney(sale.totalAmount);
  const method = methodOf(choice);
  const path = cardPathOf(choice);
  // Mientras hay que volver a elegir —o todavía no se eligió (P-081-C1)—, ningún método se ve elegido: la respuesta
  // no se da por hecha.
  const picked = !mustPick && !props.unpicked;
  const working = busy && !canceling;

  // Lo que el botón principal dice mientras no se puede tocar. El giro solo va cuando esta pantalla
  // de verdad está esperando una respuesta: registrando, confirmando, o vigilando un cobro en duda
  // (`canVerify`). Una venta retomada con el cobro sin resolver no la vigila nadie desde aquí: el
  // botón lo dice en palabras, sin girar.
  // Con el cobro ya confirmado no se registra nada: lo que se espera es que la venta se complete.
  const waiting = confirming ? 'Confirmando el cobro con el servidor…' : working ? (paid ? 'Completando la venta…' : 'Registrando…') : undefined;
  const spinning = waiting !== undefined || (locked && props.canVerify);

  // Lo que aparece al elegir —el segundo paso de Tarjeta, o el panel del método— puede nacer debajo
  // del pie pegado. Se sube lo justo para que quede a la vista: quien tocó «Tarjeta» tiene que VER
  // que falta decir cómo se cobra. Solo después de un toque: al abrir, la pantalla empieza arriba.
  const step = useRef<HTMLFieldSetElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const foot = useRef<HTMLDivElement>(null);
  // P-081 (FI-122): también al volver a tocar el método que ya estaba elegido (`revealTick`): Efectivo viene elegido al
  // abrir y su panel nace bajo el pie; tocarlo tiene que llevar a «Recibí», no quedarse quieto.
  const { revealTick, payLevel } = props;
  const shown = useRef({ choice, mustPick, revealTick });
  useEffect(() => {
    if (shown.current.choice === choice && shown.current.mustPick === mustPick && shown.current.revealTick === revealTick) return;
    shown.current = { choice, mustPick, revealTick };
    const target = choice === 'CARD_UNSET' ? step.current : panel.current;
    const scroller = target ? scrollParentOf(target) : null;
    if (!target || !scroller || !foot.current) return;
    // El borde de abajo de lo que se ve: el pie si está pegado, o el de lo que se desplaza.
    const edge = Math.min(foot.current.getBoundingClientRect().top, scroller.getBoundingClientRect().bottom);
    const top = scroller.getBoundingClientRect().top;
    // P-081: con Efectivo, si el panel entero no cabe, lo que tiene que verse junto es «Total a recibir», «Recibí» y
    // «Cambio» (el pie con «Confirmar cobro» está pegado debajo).
    let box = target.getBoundingClientRect();
    const cash = choice === 'CASH' ? target.querySelector<HTMLElement>('.n3-pos__cash') : null;
    const due = choice === 'CASH' ? target.querySelector<HTMLElement>('.n3-pos__panel-due') : null;
    if (cash && due && box.height > edge - top - 2 * REVEAL_MARGIN) {
      const from = due.getBoundingClientRect();
      const to = cash.getBoundingClientRect();
      box = { ...box, top: from.top, bottom: to.bottom, height: to.bottom - from.top } as DOMRect;
    }
    const hidden = box.bottom - edge;
    if (hidden <= 0) return;
    // P-080: si la terminal de fuera quedó elegida SOLA (único camino), el paso que lo dice tiene que
    // verse: se sube lo justo para el panel, pero nunca tanto que el paso se vaya por arriba. Elegida a
    // mano, como siempre: lo que se trae a la vista es el panel. P-081: y nunca tanto que lo que se quiere
    // ver se vaya por arriba.
    const keepStep = cardAuto && step.current ? step.current.getBoundingClientRect().top - top - REVEAL_MARGIN : Number.POSITIVE_INFINITY;
    const keepTop = box.top - top - REVEAL_MARGIN;
    scroller.scrollTop += Math.max(0, Math.min(hidden + REVEAL_MARGIN, keepStep, keepTop));
  }, [choice, mustPick, revealTick, cardAuto]);

  // P-081 · Atrás desde el panel de un método vuelve a «¿Cómo te pagan?»: la vista sube a los métodos. Lo elegido y lo
  // escrito se quedan.
  const methods = useRef<HTMLFieldSetElement>(null);
  const shownLevel = useRef(payLevel);
  useEffect(() => {
    const was = shownLevel.current;
    shownLevel.current = payLevel;
    if (was !== 'detail' || payLevel !== 'methods') return;
    const box = methods.current;
    const scroller = box ? scrollParentOf(box) : null;
    if (!box || !scroller) return;
    const above = scroller.getBoundingClientRect().top + REVEAL_MARGIN - box.getBoundingClientRect().top;
    if (above > 0) scroller.scrollTop -= above;
  }, [payLevel]);

  // Un aviso NUEVO —el lector canceló, el servidor rechazó, un error— se trae a la vista: quien tocó
  // «Confirmar cobro» abajo tiene que enterarse sin buscarlo. Al abrir no hace falta: va arriba.
  const alerts = useRef<HTMLDivElement>(null);
  const alertKey = [props.error?.message, props.notice?.title, props.notice?.detail].join('|');
  const shownAlert = useRef(alertKey);
  useEffect(() => {
    if (shownAlert.current === alertKey) return;
    shownAlert.current = alertKey;
    const box = alerts.current;
    const scroller = box ? scrollParentOf(box) : null;
    if (!box || !scroller) return;
    const above = scroller.getBoundingClientRect().top + REVEAL_MARGIN - box.getBoundingClientRect().top;
    if (above > 0) scroller.scrollTop -= above;
  }, [alertKey]);

  return (
    <section aria-labelledby="pos-checkout-title" className="n3-pos n3-pos--checkout" tabIndex={-1}>
      <div className="n3-pos__stack">
        <header className="n3-pos__step-head">
          {/* P-081: un nivel atrás, como el Atrás del teléfono. Con el cobro sin resolver o ya cobrado no hay a dónde
              volver (las salidas son las del pie), así que no se ofrece. */}
          {!locked && !paid && (
            <Button
              aria-label={payLevel === 'detail' ? 'Atrás: volver a «¿Cómo te pagan?»' : 'Atrás: volver al pedido sin cancelar la venta'}
              className="n3-pos__back"
              disabled={busy}
              iconStart={<Icon name="chevron-left" size={18} />}
              onClick={props.onBack}
              variant="tonal"
            >
              Atrás
            </Button>
          )}
          <h1 className="n3-pos__step-title" id="pos-checkout-title">Cobro</h1>
          {sale.storeName && (
            <p className="n3-pos__step-store">
              <span aria-hidden="true" className="n3-pos__step-store-icon"><Icon name="stores" size={15} /></span>
              <span>{sale.storeName}</span>
            </p>
          )}
        </header>

        {/* Los avisos van arriba, bajo el título: con el pie pegado abajo, cerca de él quedarían
            tapados —justo el del candado, que dice por qué no se puede cobrar ni cancelar—. Cuando
            aparece uno nuevo se trae a la vista (ver arriba). */}
        {(props.error || props.notice) && (
          <div className="n3-pos__alerts" ref={alerts}>
            {props.error && (
              <PosNotice
                actions={props.error.ambiguous ? <Button disabled={busy} onClick={props.onRetry} variant="secondary">Reintentar de forma segura</Button> : undefined}
                role="alert"
                tone="error"
              >
                {props.error.message}
              </PosNotice>
            )}

            {/* El desenlace del cobro con lector, tal como lo confirmó el SERVIDOR. */}
            {props.notice && (
              <PosNotice
                actions={(props.canVerify || props.notice.action) ? (
                  <>
                    {props.canVerify && <Button disabled={busy} onClick={props.onVerify} variant="secondary">Verificar cobro</Button>}
                    {props.notice.action === 'login' && <Button disabled={busy} onClick={props.onLogin} variant="secondary">Iniciar sesión en Zettle</Button>}
                    {props.notice.action === 'app-settings' && <Button disabled={busy} onClick={props.onOpenSettings} variant="secondary">Abrir ajustes</Button>}
                  </>
                ) : undefined}
                role="alert"
                title={props.notice.title}
                tone={props.notice.tone}
              >
                {props.notice.detail}
              </PosNotice>
            )}
          </div>
        )}

        <div className="n3-pos__due">
          {/* Con el cobro sin resolver no se dice «a cobrar»: puede que ya esté cobrado. */}
          <p className="n3-pos__due-label">{locked ? 'Total de la venta' : 'Total a cobrar'}</p>
          <p className="n3-pos__due-amount">{total}</p>
          <details className="n3-pos__due-lines" open={props.showLines || undefined}>
            <summary className="n3-pos__due-summary">
              <span className="n3-pos__due-pill">
                <span>{describeSaleCounts(sale.lines)}</span>
                <span aria-hidden="true" className="n3-pos__due-chevron"><Icon name="chevron-down" size={16} /></span>
              </span>
            </summary>
            <ul aria-label="Productos de la venta" className="n3-pos__sale-lines">
              {sale.lines.map((line) => (
                <li className="n3-pos__sale-line" key={line.id}>
                  <span className="n3-pos__sale-line-name">{line.quantity}× {line.productName}</span>
                  {/* Una línea sin importe se queda sin importe: no se le escribe un cero. */}
                  {line.totalAmount !== null && <span className="n3-pos__sale-line-amount">{formatMoney(line.totalAmount)}</span>}
                </li>
              ))}
            </ul>
          </details>
        </div>

        <p className="n3-pos__saved">
          {/* La etiqueta del servidor para una venta sin completar es «Pendiente de cobro»: con el
              cobro ya confirmado sería falsa, así que aquí se dice lo que es. */}
          <Badge>{paid ? 'Cobro confirmado' : sale.statusLabel}</Badge>
          <span>{paid
            ? (paidUnread ? 'Venta guardada. El cobro con tarjeta ya está confirmado.' : 'Venta guardada. El cobro con tarjeta ya está confirmado; falta completarla.')
            : locked ? 'Venta guardada. El cobro con tarjeta está sin resolver.' : 'Venta guardada. Todavía no se cobró ni se descontó inventario.'}</span>
        </p>

        {!locked && (
          <fieldset className="n3-pos__methods" ref={methods}>
            <legend className="n3-pos__ask">¿Cómo te pagan?</legend>
            <div className="n3-pos__method-grid">
              {POS_METHODS.map((option) => {
                const selected = picked && method === option.value;
                // P-081: tocar el método que YA está elegido no dispara `change`;
                // el toque (onClick) igual lleva a su panel.
                return (
                  <label className={cx('n3-pos__method', selected && 'n3-pos__method--on')} key={option.value}>
                    <input
                      checked={selected}
                      className="n3-pos__method-input"
                      disabled={busy}
                      name="pos-payment-method"
                      onChange={() => props.onMethod(option.value)}
                      onClick={() => { if (selected) props.onMethod(option.value); }}
                      type="radio"
                      value={option.value}
                    />
                    <span className="n3-pos__method-box">
                      <PosMethodTile method={option.value} />
                      <span className="n3-pos__method-label">{option.label}</span>
                      {selected && <span aria-hidden="true" className="n3-pos__method-check"><Icon name="check" size={12} /></span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}

        {/* B0-03: el segundo paso de Tarjeta. El lector solo se ofrece donde existe. */}
        {!locked && picked && method === 'CARD' && (
          <fieldset className="n3-pos__paths" ref={step}>
            <legend className="n3-pos__paths-legend">{POS_CARD_STEP.legend}</legend>
            {props.readerOffered && (
              <label className={cx('n3-pos__path', path === 'reader' && 'n3-pos__path--on')}>
                <input checked={path === 'reader'} className="n3-pos__path-input" disabled={busy} name="pos-card-path" onChange={() => props.onCardPath('reader')} onClick={() => { if (path === 'reader') props.onCardPath('reader'); }} type="radio" value="reader" />
                <span className="n3-pos__path-box">
                  <span className="n3-pos__path-label">{POS_CARD_STEP.reader}</span>
                  <span className="n3-pos__path-hint">{POS_CARD_STEP.readerHint}</span>
                  {/* El modo del lector se sigue diciendo: el build piloto cobra dinero real. */}
                  <span className={cx('n3-pos__path-hint', props.readerHint.live && 'n3-pos__path-hint--live')}>{props.readerHint.text}</span>
                </span>
              </label>
            )}
            <label className={cx('n3-pos__path', path === 'external' && 'n3-pos__path--on')}>
              <input checked={path === 'external'} className="n3-pos__path-input" disabled={busy} name="pos-card-path" onChange={() => props.onCardPath('external')} onClick={() => { if (path === 'external') props.onCardPath('external'); }} type="radio" value="external" />
              <span className="n3-pos__path-box">
                <span className="n3-pos__path-label">{POS_CARD_STEP.external}</span>
                <span className="n3-pos__path-hint">{POS_CARD_STEP.externalHint}</span>
              </span>
            </label>
          </fieldset>
        )}

        {!locked && picked && choice === 'CARD_READER' && (
          <div aria-live="polite" className="n3-pos__panel" ref={panel}>
            {working ? <PosSpinner slow={confirming} /> : <PosMethodTile large method="CARD" />}
            <h2 className="n3-pos__panel-title">{confirming ? 'Confirmando el cobro con el servidor…' : working ? 'Cobro con lector en curso' : POS_CARD_STEP.reader}</h2>
            <p className="n3-pos__panel-amount">{total}</p>
            <p className="n3-pos__panel-text">{working ? 'Sigue las indicaciones del lector. No cierres esta pantalla mientras se cobra.' : 'El cobro se abrirá en el lector.'}</p>
          </div>
        )}

        {!locked && picked && choice !== 'CARD_READER' && choice !== 'CARD_UNSET' && (
          <MethodPanel method={choice} panelRef={panel} total={total}>
            {choice === 'CASH' && <CashReceived busy={busy} cash={props.cash} onCash={props.onCash} />}
          </MethodPanel>
        )}
      </div>

      <div className="n3-pos__foot" ref={foot}>
        {props.backBlocked && (
          <p className="n3-pos__why n3-pos__why--warn" role="status">
            {/* Con el cobro ya confirmado no hay «un cobro en proceso»: lo que falta es la venta. */}
            {paid
              ? (working
                ? 'Se está completando la venta. Espera a que termine antes de salir.'
                : paidUnread
                  ? 'El cobro de esta venta ya está confirmado. Toca «Completar la venta» o «Dejar pendiente y seguir vendiendo».'
                  : 'Esta venta ya está cobrada y falta completarla. Toca «Completar la venta» o «Dejar pendiente y seguir vendiendo».')
              : locked || working
                ? <>Hay un cobro en proceso. Espera a conocer el resultado antes de salir{locked ? ', o toca «Dejar pendiente y seguir vendiendo».' : '.'}</>
                // P-081: Atrás no saca del cobro una venta que ya pasó por el lector (o hay que volver a elegir).
                : 'Esta venta ya pasó por el lector. Para salir, elige cómo te pagaron y confírmalo, o toca «Cancelar venta».'}
          </p>
        )}
        {props.why && <p className="n3-pos__why" role="status">{props.why}</p>}
        <Button className="n3-pos__cta" disabled={props.confirmDisabled} fullWidth loading={spinning} loadingLabel={waiting ?? LOCKED_LABEL} onClick={props.onConfirm} size="lg" variant="primary">
          {paid ? COMPLETE_LABEL : locked ? LOCKED_LABEL : `Confirmar cobro · ${total}`}
        </Button>
        {/* Con el cobro sin resolver, confirmar y cancelar están apagados: esta es la salida. */}
        {locked && <Button disabled={busy} fullWidth onClick={props.onLeavePending} variant="secondary">Dejar pendiente y seguir vendiendo</Button>}
        <Button disabled={props.cancelDisabled} fullWidth loading={canceling} loadingLabel="Cancelando…" onClick={props.onCancel} variant="danger">Cancelar venta</Button>
      </div>
    </section>
  );
}

/** Efectivo, Transferencia, Otro o la tarjeta de terminal externa: qué se registra y qué comprobar. */
function MethodPanel({ method, total, panelRef, children }: { method: NativePaymentMethod; total: string; panelRef: RefObject<HTMLDivElement>; children?: ReactNode }) {
  const panel = describeMethodPanel(method);
  return (
    <div className="n3-pos__panel" ref={panelRef}>
      <PosMethodTile large method={method} />
      <h2 className="n3-pos__panel-title">{panel.title}</h2>
      {panel.hint && <p className="n3-pos__panel-hint">{panel.hint}</p>}
      <p className="n3-pos__panel-due">{panel.amountLabel}: <strong className="n3-pos__panel-due-amount">{total}</strong></p>
      {children}
      <p className="n3-pos__panel-note">
        <span aria-hidden="true" className="n3-pos__panel-note-icon"><Icon name="info" size={17} /></span>
        <span>{panel.note}</span>
      </p>
    </div>
  );
}

/**
 * P-080 · «Recibí» y lo que resulta: el cambio, o cuánto falta. P-081-C1: arranca VACÍO y es opcional —vacío se cobra
 * el total; escrito, calcula el cambio—; al enfocarlo se selecciona todo, para escribir encima sin borrar a mano. Lo
 * que resulta va enlazado al campo: al traerlo a la vista con el teclado abierto se trae con él.
 */
function CashReceived({ cash, busy, onCash }: { cash: { text: string; received: PosCashReceived }; busy: boolean; onCash: (text: string) => void }) {
  const resultId = `${useId()}-result`;
  const { received } = cash;
  // Codex final (medido en 390 × 844 con el teclado abierto): al pasar de «alcanza» a «no alcanza» —o a vacío o mal
  // escrito— el pie pegado crece con su porqué y tapaba «Faltan». Mientras se escribe aquí, el campo y lo que resulta
  // se vuelven a traer a la vista con el mismo revelado del shell (P-079), que ya descuenta el pie.
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const field = input.current;
    if (!field || document.activeElement !== field) return;
    revealElement(field, { shellScroller: scrollParentOf(field), companion: fieldCompanion(field) });
  }, [received.kind]);
  return (
    <div className="n3-pos__cash">
      <Field error={received.kind === 'invalid' ? POS_CASH.invalid : undefined} hint={received.kind === 'empty' ? POS_CASH.optional : undefined} label={POS_CASH.label}>
        <Input
          aria-describedby={resultId}
          autoComplete="off"
          disabled={busy}
          enterKeyHint="done"
          inputMode="decimal"
          onChange={(event) => onCash(event.target.value)}
          onFocus={(event) => event.target.select()}
          placeholder={POS_CASH.placeholder}
          prefix="$"
          ref={input}
          type="text"
          value={cash.text}
        />
      </Field>
      <p aria-live="polite" className={cx('n3-pos__cash-result', received.kind === 'short' && 'n3-pos__cash-result--short')} id={resultId}>
        {received.kind === 'enough' && <>{POS_CASH.change}: <strong className="n3-pos__cash-amount">{formatMoney(received.change)}</strong></>}
        {received.kind === 'short' && <>{POS_CASH.missing}: <strong className="n3-pos__cash-amount">{formatMoney(received.missing)}</strong></>}
      </p>
    </div>
  );
}
